<template>
  <div class="librarian-edit-container">
    <van-form @submit="onSubmit" class="edit-form" v-if="!loading && librarianData">
      <van-cell-group inset title="编辑馆员信息" class="form-cell-group">
        <van-field
          v-model="formData.name"
          name="name"
          label="姓名"
          placeholder="请输入姓名"
          :rules="[{ required: true, message: '姓名不能为空' }]"
          label-width="5em"
          clearable
        >
          <template #button> <!-- 使用 #button 插槽在输入框右侧放置按钮 -->
            <van-button
              size="small"
              type="danger"
              icon="delete-o"
              plain
              @click="confirmDeleteLibrarian"
              :loading="isDeleting"
              :disabled="isSubmittingForm"
              class="delete-name-button"
            >
              删除
            </van-button>
          </template>
        </van-field>

        <van-field
          v-model="formData.birthday"
          name="birthday"
          label="出生年月"
          placeholder="格式 YYYY.M 或 YYYY.MM"
          :rules="[{ pattern: /^\d{4}\.(0?[1-9]|1[0-2])$/, message: '格式 YYYY.M 或 YYYY.MM' }]"
          label-width="5em"
          clearable
          maxlength="7"
        />
        <van-field
          v-model="formData.phone"
          name="phone"
          label="电话"
          type="textarea"
          rows="2"
          autosize
          placeholder="请输入电话，多个可用逗号等分隔"
          label-width="5em"
          clearable
        />
      </van-cell-group>

      <div class="form-actions-container"> <!-- 主操作按钮区域 -->
        <van-button round block type="primary" native-type="submit" :loading="isSubmittingForm" class="action-button save-button">
          保存修改
        </van-button>
        <van-button round block plain type="default" @click="goBack" class="action-button cancel-button">
          取消
        </van-button>
        <!-- 原来的底部删除按钮已移除 -->
      </div>
    </van-form>
    <!-- ... (加载中和加载失败提示与上一版本一致) ... -->
     <div v-if="loading" style="text-align: center; padding-top: 20px;"> <van-loading type="spinner" /> <p>加载中...</p> </div>
     <div v-if="!loading && !librarianData && !initialLoadFailed" style="text-align: center; padding-top: 20px;"> <p>未能加载馆员信息，或该馆员可能已被删除。</p> <van-button type="default" @click="goBack">返回列表</van-button> </div>
     <div v-if="initialLoadFailed" style="text-align: center; padding-top: 20px; color: red;"> 加载馆员信息失败，请重试。 </div>
  </div>
</template>

<script setup>
// <script setup> 部分与上一条回复中包含完整删除逻辑的版本【完全一致】
// (包括所有 ref, computed, functions 定义，例如 formData, confirmDeleteLibrarian 等)
import { ref, onMounted, reactive, h } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getLibrarianById, updateLibrarian, softDeleteLibrarian } from '../services/api';
import { showNotify, showDialog, Field as VanField } from 'vant';

const props = defineProps({ id: { type: [String, Number], required: true } });
const router = useRouter();
const loading = ref(true);
const initialLoadFailed = ref(false);
const isSubmittingForm = ref(false);
const isDeleting = ref(false);
const librarianData = ref(null);
const formData = reactive({ name: '', birthday: '', phone: '' });

const fetchLibrarianData = async () => { 
  loading.value = true; 
  initialLoadFailed.value = false; 
  try { 
    const response = await getLibrarianById(props.id); 
    if (response && response.data) { 
      librarianData.value = response.data; 
      formData.name = response.data.name || ''; 
      formData.birthday = response.data.birthday || ''; 
      formData.phone = response.data.phone || ''; 
    } else { 
      throw new Error("Librarian data not found in response"); 
    } 
  } catch (error) { 
    initialLoadFailed.value = true; 
    showNotify({ 
      type: 'danger', 
      message: `加载馆员信息失败: ${error.response?.data?.message || error.message || '未知错误'}` 
    }); 
    librarianData.value = null; 
  } finally { 
    loading.value = false; 
  } 
};
onMounted(() => { fetchLibrarianData(); });
const onSubmit = async () => { 
  isSubmittingForm.value = true; 
  try { 
    const updateData = { 
      name: formData.name, 
      birthday: formData.birthday, 
      phone: formData.phone 
    }; 
    const response = await updateLibrarian(props.id, updateData); 
    if (response && response.status === 200) { 
      showNotify({ 
        type: 'success', 
        message: response.data.message || '馆员信息更新成功' 
      }); 
      router.push({ name: 'LibrarianList' }); 
    } else { 
      showNotify({ 
        type: 'danger', 
        message: response?.data?.message || '更新失败' 
      }); 
    } 
  } catch (error) { 
    showNotify({ 
      type: 'danger', 
      message: `更新API错误: ${error.response?.data?.message || error.message || '未知API错误'}` 
    }); 
  } finally { 
    isSubmittingForm.value = false; 
  } 
};
const goBack = () => { router.push({ name: 'LibrarianList' }); };
const confirmDeleteLibrarian = async () => { if (!librarianData.value || !librarianData.value.name) { showNotify({ type: 'warning', message: '无法获取馆员信息进行删除' }); return; } if (isDeleting.value) return; try { await showDialog({ title: '确认删除', message: () => h('div', [ h('span', '你确定要删除 '), h('strong', librarianData.value.name), h('span', ' 馆员吗？'), ]), showCancelButton: true, confirmButtonText: '确定删除', cancelButtonText: '取消', }); const passwordInput = ref(''); await showDialog({ title: '密码确认', message: () => h('div', { style: { padding: '10px 0' } }, [ h('p', { style: { marginBottom: '10px', fontSize: 'var(--van-font-size-md)' } }, `输入密码确认删除馆员 “<strong>${librarianData.value.name}</strong>”！`), h(VanField, { modelValue: passwordInput.value, 'onUpdate:modelValue': (val) => { passwordInput.value = val; }, type: 'password', placeholder: '请输入您的登录密码', rules: [{ required: true, message: '密码不能为空' }], }) ]), showCancelButton: true, confirmButtonText: '最终确认删除', cancelButtonText: '取消', beforeClose: async (action) => { if (action === 'confirm') { if (!passwordInput.value || passwordInput.value.trim() === '') { showNotify({ type: 'warning', message: '请输入密码以确认删除' }); return false; } return true; } return true; } }); const operatorPassword = passwordInput.value; isDeleting.value = true; const response = await softDeleteLibrarian(props.id, { password: operatorPassword }); if (response && response.status === 200) { showNotify({ type: 'success', message: `馆员 “${librarianData.value.name}” 已删除！` }); router.push({ name: 'LibrarianList' }); } else { showNotify({ type: 'danger', message: response?.data?.message || '删除失败' }); } } catch (actionOrError) { if (actionOrError === 'cancel' || (actionOrError && actionOrError.message && actionOrError.message.toLowerCase().includes('cancel'))) { console.log('Librarian deletion cancelled by user.'); } else if (actionOrError) { showNotify({ type: 'danger', message: `删除操作失败: ${actionOrError.response?.data?.message || actionOrError.message || '未知错误'}` }); } } finally { isDeleting.value = false; } };
</script>

<style scoped>
.librarian-edit-container {
  padding: var(--van-padding-sm);
}
.edit-form {
  margin-top: 20px;
}
.form-cell-group .van-field__label {
  font-weight: 500;
}
.form-cell-group .van-cell-group__title {
    font-size: var(--van-font-size-md);
    padding: var(--van-padding-md) var(--van-padding-md) var(--van-padding-sm) var(--van-padding-md);
}
.form-actions-container { /* 主操作按钮容器 */
  margin: 30px 16px;
}
.action-button { /* 通用操作按钮样式 */
    font-weight: bold;
}
.save-button {
    /* 主色按钮，已有 type="primary" */
}
.cancel-button {
    margin-top: 15px;
}
/* 删除按钮现在放在 van-field 的插槽中，可以有特定样式 */
.delete-name-button.van-button--small { /* 确保样式针对 small 尺寸 */
    height: auto; /* 让按钮高度自适应内容，或设置一个固定值 */
    padding: 0 8px; /* 调整内边距 */
    line-height: normal; /* 重置可能影响图标和文字对齐的行高 */
    border-color: var(--van-danger-color);
    color: var(--van-danger-color);
}
.delete-name-button.van-button--danger.van-button--plain { /* 如果使用 plain 属性 */
    /* Vant plain danger 按钮默认样式可能就是红色边框红色文字 */
}
.delete-name-button .van-icon { /* 如果按钮内有图标 */
    font-size: 1em; /* 让图标大小与文字协调 */
}
</style>